import { Gauge, Zap, Coins, FileCheck, UserCog, ShieldCheck } from "lucide-react";

const FEATURES = [
    { icon: Gauge, title: "Wholesale Pricing", copy: "Institutional-grade rates negotiated directly with banking partners — passed transparently.", color: "gold" },
    { icon: Zap, title: "Velocity Decisions", copy: "Pre-approvals in minutes, capital disbursed in as little as 24 hours.", color: "coral" },
    { icon: Coins, title: "High-Ticket Capacity", copy: "Up to AED 60M for enterprises, AED 5M private credit — without forcing a checkbox.", color: "forest" },
    { icon: FileCheck, title: "Digital Choreography", copy: "KYC, signing, valuations — orchestrated digitally with discreet couriers when required.", color: "sky" },
    { icon: UserCog, title: "Single-Point Advisor", copy: "One named, accountable expert from origination through final repayment.", color: "gold" },
    { icon: ShieldCheck, title: "Sovereign-Grade Security", copy: "Encrypted end-to-end, ring-fenced custody. Discretion is a contract.", color: "forest" },
];

const COLORS = {
    gold: { ring: "border-gold/40 group-hover:border-gold group-hover:bg-gold/10", icon: "text-gold" },
    forest: { ring: "border-forest/40 group-hover:border-forest group-hover:bg-forest/10", icon: "text-forest" },
    coral: { ring: "border-coral/40 group-hover:border-coral group-hover:bg-coral/10", icon: "text-coral" },
    sky: { ring: "border-sky/40 group-hover:border-sky group-hover:bg-sky/10", icon: "text-sky" },
};

const ADVISOR_IMG =
    "https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=900&q=80";

export default function WhyUs() {
    return (
        <section
            id="why"
            data-testid="why-us-section"
            className="relative py-20 md:py-28 bg-white"
        >
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                <div className="grid md:grid-cols-12 gap-8 mb-14">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="w-8 h-px bg-gold" />
                            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                                The QuickFin Standard
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-[1] tracking-tight font-medium text-ink">
                            Engineered for clarity.<br />
                            Built for <em className="italic text-gold font-normal">velocity.</em>
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:col-start-9 flex items-end">
                        <p className="text-ink-muted font-light leading-relaxed text-lg">
                            We replaced opaque forms, hidden fees and weeks of waiting with a discreet, design-first experience.
                        </p>
                    </div>
                </div>

                {/* 4-col asymmetric grid: 6 features + 1 image + 1 advisor card */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {/* Feature 1 - gold */}
                    {renderFeature(FEATURES[0], 0)}
                    {/* Image card spans 2 cols */}
                    <div
                        data-testid="why-image-card"
                        className="lg:col-span-2 relative aspect-[16/10] sm:aspect-auto sm:row-span-1 overflow-hidden border border-cream-deep group"
                    >
                        <img
                            src={ADVISOR_IMG}
                            alt="Advisor"
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-ink/90 via-ink/30 to-transparent" />
                        <div className="absolute top-4 left-4 font-mono text-[9px] uppercase tracking-[0.3em] text-gold bg-ink/60 backdrop-blur px-2 py-1">
                            One Advisor · One File
                        </div>
                        <div className="absolute bottom-5 left-5 right-5">
                            <div className="font-serif text-2xl md:text-3xl text-white leading-tight">
                                Not a call centre.<br />
                                <em className="italic text-gold font-normal">A named expert.</em>
                            </div>
                            <div className="mt-4 flex items-center gap-2">
                                <img src="https://i.pravatar.cc/64?img=32" alt="" className="w-9 h-9 rounded-full border-2 border-gold object-cover" />
                                <img src="https://i.pravatar.cc/64?img=47" alt="" className="w-9 h-9 rounded-full border-2 border-gold object-cover -ml-3" />
                                <img src="https://i.pravatar.cc/64?img=12" alt="" className="w-9 h-9 rounded-full border-2 border-gold object-cover -ml-3" />
                                <a
                                    href="#contact"
                                    data-testid="why-talk-cta"
                                    className="ml-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white hover:text-gold gold-underline"
                                >
                                    Talk to one →
                                </a>
                            </div>
                        </div>
                    </div>
                    {/* Feature 2 - coral */}
                    {renderFeature(FEATURES[1], 1)}

                    {/* Solid colored cards in second row */}
                    <div
                        data-testid="feature-card-2"
                        className="group relative bg-forest text-white p-6 hover:bg-forest-deep transition-colors"
                    >
                        <div className="absolute top-4 right-4 font-mono text-[9px] tracking-[0.3em] text-white/40">03</div>
                        <div className="w-10 h-10 border border-white/40 flex items-center justify-center mb-5 group-hover:bg-white/10 transition-all">
                            <Coins size={16} className="text-white" />
                        </div>
                        <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-white mb-2">
                            {FEATURES[2].title}
                        </h3>
                        <p className="text-white/80 font-light leading-relaxed text-base">
                            {FEATURES[2].copy}
                        </p>
                    </div>
                    {renderFeature(FEATURES[3], 3)}
                    {renderFeature(FEATURES[4], 4)}
                    <div
                        data-testid="feature-card-5"
                        className="group relative bg-coral text-white p-6 hover:bg-coral-soft transition-colors"
                    >
                        <div className="absolute top-4 right-4 font-mono text-[9px] tracking-[0.3em] text-white/50">06</div>
                        <div className="w-10 h-10 border border-white/40 flex items-center justify-center mb-5 group-hover:bg-white/10 transition-all">
                            <ShieldCheck size={16} className="text-white" />
                        </div>
                        <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-white mb-2">
                            {FEATURES[5].title}
                        </h3>
                        <p className="text-white/85 font-light leading-relaxed text-base">
                            {FEATURES[5].copy}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function renderFeature(f, i) {
    const Icon = f.icon;
    const c = COLORS[f.color];
    return (
        <div
            key={f.title}
            data-testid={`feature-card-${i}`}
            className="group relative bg-white p-6 border border-cream-deep hover:border-gold/40 hover:shadow-xl transition-all duration-500"
        >
            <div className="absolute top-4 right-4 font-mono text-[9px] tracking-[0.3em] text-ink-muted/40">
                0{i + 1}
            </div>
            <div className={`w-10 h-10 border flex items-center justify-center mb-5 transition-all ${c.ring}`}>
                <Icon size={16} className={c.icon} />
            </div>
            <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink mb-2">
                {f.title}
            </h3>
            <p className="text-ink-muted font-light leading-relaxed text-base">
                {f.copy}
            </p>
        </div>
    );
}
